import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  HOTEL_MEAL_PLAN_LABELS,
  HOTEL_DEPARTURE_LABELS,
  TRIP_TYPE_LABELS,
  TRIP_DATE_LIFECYCLE_BADGES,
  TRIP_DATE_LIFECYCLE_LABELS,
  LEAD_SOURCE_CALLBACK,
  type LeadWithRelations,
  type Trip,
  type User,
} from "@ckgo2/shared/types";
import toast from "react-hot-toast";
import { formatPrice, sortCzech } from "@ckgo2/shared/utils";
import { formatDate } from "@/lib/format";
import { LEAD_STATUS_CONFIG } from "@/lib/statusConfig";
import i18n, { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import { useEditPage } from "@/hooks/useEditPage";
import DestinationPicker from "@/components/common/DestinationPicker";
import { LeadKindBadge } from "./leadKind";
import { PrivateDateBadge } from "@/components/trips/TripDatesEditor";
import CommunicationTimeline from "@/components/orders/CommunicationTimeline";
import RecentCallsPanel from "@/components/common/RecentCallsPanel";
import ErrorBanner from "@/components/common/ErrorBanner";
import SaveSuccessIndicator from "@/components/common/SaveSuccessIndicator";
import AiReviewPanel, { type ParseResult } from "@/components/common/AiReviewPanel";
import { useAgent } from "@/components/agent/AgentProvider";
import { ArrowLeft, Loader2, Save, ShoppingCart, Sparkles, Trash2 } from "lucide-react";
import type { Communication } from "@ckgo2/shared/types";

// Enriched qualification chips (Block 5.7 Řez 2 — Veru's wishlist fields).
// Module scope is safe: a language switch reloads the page.
const PROFILE_LABELS: Record<string, Record<string, string>> = {
  tripType: {
    fit: i18n.t("crm.leadDetail.profile.fit"),
    guided: i18n.t("crm.leadDetail.profile.guided"),
    stay: i18n.t("crm.leadDetail.profile.stay"),
    service: i18n.t("crm.leadDetail.profile.service"),
  },
  flightClass: {
    economy: i18n.t("crm.leadDetail.profile.economy"),
    premium: i18n.t("crm.leadDetail.profile.premium"),
    business: i18n.t("crm.leadDetail.profile.business"),
  },
  dateFlexibility: {
    fixed: i18n.t("crm.leadDetail.profile.fixedDates"),
    plus_minus_week: i18n.t("crm.leadDetail.profile.plusMinusWeek"),
    flexible: i18n.t("crm.leadDetail.profile.flexible"),
  },
  travelExperience: {
    first_asia: i18n.t("crm.leadDetail.profile.firstAsia"),
    experienced: i18n.t("crm.leadDetail.profile.experienced"),
    expert: i18n.t("crm.leadDetail.profile.expert"),
  },
};

/**
 * Popisky kanálů, kterými k nám poptávka přišla (`leads.source`). Sloupec je volný text
 * (i historická data a ruční zápisy), takže se neznámá hodnota vypisuje SYROVÁ — mlčet
 * o kanálu je horší než ukázat `web_custom`.
 */
const SOURCE_LABELS: Record<string, string> = {
  web_form: i18n.t("crm.leadDetail.sources.web_form"),
  web_custom: i18n.t("crm.leadDetail.sources.web_custom"),
  contact_form: i18n.t("crm.leadDetail.sources.contact_form"),
  email: i18n.t("crm.leadDetail.sources.email"),
  phone: i18n.t("crm.leadDetail.sources.phone"),
  manual: i18n.t("crm.leadDetail.sources.manual"),
  [LEAD_SOURCE_CALLBACK]: i18n.t("crm.leadDetail.sources.web_callback"),
};

interface LeadForm {
  status: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  tripId: string;
  destinationId: string;
  assignedTo: string;
  requestedDate: string;
  paxAdults: number;
  paxChildren: number;
  estimatedValue: number | null;
  currency: string;
  nextActionAt: string;
  lostReason: string;
  customerNotes: string;
  internalNotes: string;
}

const EMPTY: LeadForm = {
  status: "received",
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  tripId: "",
  destinationId: "",
  assignedTo: "",
  requestedDate: "",
  paxAdults: 2,
  paxChildren: 0,
  estimatedValue: null,
  currency: "CZK",
  nextActionAt: "",
  lostReason: "",
  customerNotes: "",
  internalNotes: "",
};

type LeadDetail = LeadWithRelations & { communications?: Communication[] };

export default function LeadDetailPage() {
  const { t } = useTranslation();
  const { openChat, enabled: agentEnabled } = useAgent();
  // "Use as reply" on the AI first-reply card seeds the timeline's reply composer.
  const [prefillReply, setPrefillReply] = useState<string | undefined>();

  // Edit/create plumbing (params, query, form, save mutation, navigation) via the shared hook.
  // After save the hook stays on the lead (create replaces the URL with the new lead's detail).
  // Ukládá se jen přes `onSubmit`: ten při souběžné úpravě (409) nabídne přepsání jako
  // ostatní editory; `save` slouží jen ke stavu (`isPending`, `isSuccess`, `error`).
  const {
    isNew,
    isLoading,
    form,
    saveMutation: save,
    onSubmit,
    navigate,
    itemData: lead,
  } = useEditPage<LeadForm, LeadDetail>({
    resourceKey: "lead",
    listKey: "leads",
    apiPath: "/api/admin/leads",
    navigateTo: "/leads",
    defaultValues: EMPTY,
    transformOnLoad: (l) => ({
      status: l.status,
      contactName: l.contactName ?? "",
      contactEmail: l.contactEmail ?? "",
      contactPhone: l.contactPhone ?? "",
      tripId: l.tripId ?? "",
      destinationId: l.destinationId ?? "",
      assignedTo: l.assignedTo ?? "",
      requestedDate: l.requestedDate ?? "",
      paxAdults: l.paxAdults,
      paxChildren: l.paxChildren,
      estimatedValue: l.estimatedValue ?? null,
      currency: l.currency,
      nextActionAt: l.nextActionAt ?? "",
      lostReason: l.lostReason ?? "",
      customerNotes: l.customerNotes ?? "",
      internalNotes: l.internalNotes ?? "",
    }),
    transformOnSave: (data) => ({
      ...data,
      estimatedValue: data.estimatedValue === null ? null : Number(data.estimatedValue),
      paxAdults: Number(data.paxAdults),
      paxChildren: Number(data.paxChildren),
    }),
  });
  const { register, handleSubmit, watch, setValue, control } = form;

  // Hard delete (junk leads — mailer-daemon, spam). FKs on communications/orders are
  // `set null`, so linked history survives; only the lead record goes away.
  const queryClient = useQueryClient();
  const deleteLead = useMutation({
    mutationFn: () => api.delete(`/api/admin/leads/${lead?.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      navigate("/leads");
    },
    onError: (error: Error) => {
      toast.error(error.message || t("crm.leadDetail.deleteFailed"));
    },
  });

  // Apply an AI parse result to the form (only non-empty values; never clobbers).
  const applyAi = (r: ParseResult) => {
    const f = r.fields;
    const setStr = (name: keyof LeadForm, v: unknown) => {
      if (v !== "" && v != null) setValue(name, v as never);
    };
    setStr("contactName", f.contactName?.value);
    setStr("contactEmail", f.contactEmail?.value);
    setStr("contactPhone", f.contactPhone?.value);
    setStr("tripId", f.tripId?.value);
    setStr("destinationId", f.destinationId?.value);
    setStr("requestedDate", f.requestedDate?.value);
    setStr("customerNotes", f.notes?.value);
    if (f.paxAdults?.value) setValue("paxAdults", Number(f.paxAdults.value));
    if (f.paxChildren?.value) setValue("paxChildren", Number(f.paxChildren.value));
  };

  const { data: usersData } = useQuery({
    queryKey: ["users"],
    queryFn: () => api.get<ApiResponse<User[]>>("/api/admin/users"),
  });
  const { data: tripsData } = useQuery({
    queryKey: ["trips-for-lead"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  const status = watch("status");
  // The lead carries its own currency (resolved server-side from finance.defaultCurrency) — show it
  // instead of a hardcoded one, which would be misleading on a foreign-currency lead.
  const currency = watch("currency") || "CZK";

  if (!isNew && isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="text-primary-600 h-8 w-8 animate-spin" />
      </div>
    );
  }

  const cfg =
    LEAD_STATUS_CONFIG[status as keyof typeof LEAD_STATUS_CONFIG] ?? LEAD_STATUS_CONFIG.received;
  // Výběry obchodníka a zájezdu česky abecedně (F1.8); `sortCzech` vrací kopii, keš se nemění.
  const users = sortCzech(usersData?.data ?? [], (u) => u.name);
  const trips = sortCzech(tripsData?.data ?? [], (trip) => trip.title);

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/leads")} className="btn-ghost btn-sm">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {isNew ? t("crm.leads.newLead") : lead?.leadNumber}
              </h1>
              {!isNew && <span className={`badge ${cfg.className}`}>{cfg.label}</span>}
              {!isNew && lead && <LeadKindBadge kind={lead.kind} />}
            </div>
            {!isNew && lead && (
              <p className="text-sm text-gray-500">
                {t("crm.leadDetail.createdAt", { date: formatDate(lead.createdAt) })}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && lead && (
            <button
              type="button"
              onClick={() => {
                if (
                  window.confirm(t("crm.leadDetail.deleteConfirm", { number: lead.leadNumber }))
                ) {
                  deleteLead.mutate();
                }
              }}
              disabled={deleteLead.isPending}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
            >
              {deleteLead.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t("common.actions.delete")}
            </button>
          )}
          {!isNew && agentEnabled && (
            <button
              type="button"
              onClick={() =>
                openChat(
                  "Build a trip offer for this inquiry: suggest a fitting departure with real availability and a price estimate from our catalog, then draft a short pitch I can send. Use only grounded data.",
                )
              }
              className="border-primary-200 text-primary-700 hover:bg-primary-50 dark:border-primary-800 dark:text-primary-300 dark:hover:bg-primary-900/20 inline-flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium"
            >
              <Sparkles className="h-4 w-4" />
              {t("crm.leadDetail.buildOffer")}
            </button>
          )}
          <SaveSuccessIndicator isSuccess={save.isSuccess} />
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={save.isPending}
            className="btn-primary"
          >
            {save.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {t("common.actions.save")}
          </button>
        </div>
      </div>

      {/* Uložení poptávky hlásilo chybu jen do konzole — bez tohoto banneru zůstal
          odmítnutý submit na stránce úplně němý. Validační banner sem NEPATŘÍ: stránka
          nemá `schema:`, takže jediné chyby ve formuláři jsou zrcadlené serverové (a ty
          už vypisuje `ErrorBanner`). */}
      <ErrorBanner error={save.error} />

      {/* AI assist — only when creating a new lead from a pasted inquiry. */}
      {isNew && <AiReviewPanel onApply={applyAi} />}

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main form */}
        <form className="space-y-6 lg:col-span-2">
          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t("crm.leadDetail.contactSection")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("crm.fields.name")}>
                <input className="input" {...register("contactName")} />
              </Field>
              <Field label={t("common.fields.email")}>
                <input className="input" type="email" {...register("contactEmail")} />
              </Field>
              <Field label={t("common.fields.phone")}>
                <input className="input" {...register("contactPhone")} />
              </Field>
              <Field label={t("crm.leadDetail.assignedAgent")}>
                <select className="input" {...register("assignedTo")}>
                  <option value="">{t("crm.leadDetail.unassignedOption")}</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t("crm.leadDetail.interestSection")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("common.fields.trip")}>
                <select className="input" {...register("tripId")}>
                  <option value="">{t("crm.leadDetail.noneCustomOption")}</option>
                  {trips.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.title}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("common.fields.destination")}>
                <Controller
                  name="destinationId"
                  control={control}
                  render={({ field }) => (
                    <DestinationPicker
                      value={field.value}
                      onChange={(id) => field.onChange(id ?? "")}
                    />
                  )}
                />
              </Field>
              <Field label={t("crm.leadDetail.requestedDate")}>
                <input className="input" type="date" {...register("requestedDate")} />
              </Field>
              <Field label={t("crm.leadDetail.estimatedValue", { currency })}>
                <input
                  className="input"
                  type="number"
                  {...register("estimatedValue", { valueAsNumber: true })}
                />
              </Field>
              <Field label={t("crm.leadDetail.adults")}>
                <input
                  className="input"
                  type="number"
                  min={1}
                  {...register("paxAdults", { valueAsNumber: true })}
                />
              </Field>
              <Field label={t("crm.leadDetail.children")}>
                <input
                  className="input"
                  type="number"
                  min={0}
                  {...register("paxChildren", { valueAsNumber: true })}
                />
              </Field>
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {t("crm.leadDetail.pipelineSection")}
            </h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t("common.fields.status")}>
                <select className="input" {...register("status")}>
                  {LEAD_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {enumLabel(LEAD_STATUS_LABELS, s)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("crm.leadDetail.nextAction")}>
                <input className="input" type="date" {...register("nextActionAt")} />
              </Field>
              {status === "lost" && (
                <Field label={t("crm.leadDetail.lostReason")} full>
                  <input className="input" {...register("lostReason")} />
                </Field>
              )}
            </div>
            <Field label={t("crm.leadDetail.customerNotes")} full>
              <textarea className="input resize-none" rows={2} {...register("customerNotes")} />
            </Field>
            <Field label={t("crm.leadDetail.internalNotes")} full>
              <textarea className="input resize-none" rows={2} {...register("internalNotes")} />
            </Field>
          </div>
        </form>

        {/* Sidebar */}
        <div className="space-y-6">
          {!isNew && lead && (
            <div className="card space-y-3">
              <h2 className="font-semibold text-gray-900 dark:text-white">
                {t("crm.leadDetail.conversionSection")}
              </h2>
              {lead.convertedOrderId ? (
                <Link
                  to={`/orders/${lead.convertedOrderId}`}
                  className="btn-ghost w-full justify-center"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t("crm.leadDetail.viewOrder")}
                </Link>
              ) : (
                <Link
                  to={`/orders/new?leadId=${lead.id}`}
                  className="btn-primary w-full justify-center"
                >
                  <ShoppingCart className="h-4 w-4" />
                  {t("crm.leadDetail.createOrder")}
                </Link>
              )}
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">{t("crm.leadDetail.leadScore")}</span>
                <span className="flex items-center gap-1 font-medium text-gray-900 dark:text-white">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  {lead.leadScore}
                </span>
              </div>
            </div>
          )}

          {!isNew && lead && <LeadDateCard lead={lead} />}

          {/* Structured hotel choice from the inquiry (vize D3) — a snapshot taken when the
              customer asked, so it deliberately keeps the room name and surcharge of that
              moment even after the hotel's catalogue changes. */}
          {!isNew && lead?.profile?.hotel && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">
                {t("crm.leadDetail.hotel.title")}
              </h2>
              <dl className="space-y-1 text-sm">
                <Row
                  label={t("crm.leadDetail.hotel.stay")}
                  value={`${formatDate(lead.profile.hotel.stayFrom)} – ${formatDate(
                    lead.profile.hotel.stayTo,
                  )} (${t("crm.leadDetail.hotel.nights", { count: lead.profile.hotel.nights })})`}
                />
                <Row
                  label={t("crm.leadDetail.hotel.room")}
                  value={
                    lead.profile.hotel.room
                      ? withSurcharge(
                          lead.profile.hotel.room.name,
                          lead.profile.hotel.room.surcharge,
                          t("crm.leadDetail.hotel.included"),
                          currency,
                        )
                      : null
                  }
                />
                <Row
                  label={t("crm.leadDetail.hotel.meal")}
                  value={
                    lead.profile.hotel.mealPlan
                      ? withSurcharge(
                          enumLabel(HOTEL_MEAL_PLAN_LABELS, lead.profile.hotel.mealPlan.plan),
                          lead.profile.hotel.mealPlan.surcharge,
                          t("crm.leadDetail.hotel.included"),
                          currency,
                        )
                      : null
                  }
                />
                <Row
                  label={t("crm.leadDetail.hotel.departure")}
                  value={
                    lead.profile.hotel.departure
                      ? enumLabel(HOTEL_DEPARTURE_LABELS, lead.profile.hotel.departure)
                      : null
                  }
                />
              </dl>
            </div>
          )}

          {/* Poptávka BEZ zájezdu (řez 07, vize F2). „Kam" se čte VÝHRADNĚ z RELACE
              (`destinationName` z leftJoinu) — `profile.custom` žádný volný text destinace
              nemá a mít nesmí, jinak by se dva zdroje pravdy o téže věci časem rozešly.
              Typ je KATALOGOVÁ osa, proto `TRIP_TYPE_LABELS`, ne `PROFILE_LABELS.tripType`
              o pár řádků výš — stejný klíč, jiný číselník. */}
          {!isNew && lead?.profile?.custom && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">
                {t("crm.leadDetail.custom.title")}
              </h2>
              <dl className="space-y-1 text-sm">
                <Row
                  label={t("crm.leadDetail.custom.where")}
                  value={lead.destinationName ?? t("crm.leadDetail.custom.whereUnknown")}
                />
                <Row
                  label={t("crm.leadDetail.custom.window")}
                  value={formatPeriod(lead.profile.custom.dateFrom, lead.profile.custom.dateTo)}
                />
                <Row
                  label={t("crm.leadDetail.custom.durationLabel")}
                  value={
                    lead.profile.custom.durationDays
                      ? t("crm.leadDetail.custom.duration", {
                          count: lead.profile.custom.durationDays,
                        })
                      : null
                  }
                />
                <Row
                  label={t("crm.leadDetail.custom.tripType")}
                  value={
                    lead.profile.custom.tripType
                      ? enumLabel(TRIP_TYPE_LABELS, lead.profile.custom.tripType)
                      : null
                  }
                />
                <Row
                  label={t("crm.leadDetail.custom.originPath")}
                  value={lead.profile.custom.originPath}
                />
              </dl>
            </div>
          )}

          {/* Qualification chips. The condition lists the fields this card actually renders —
              a profile carrying ONLY the hotel block (its own card above) must not draw an
              empty "Trip profile" box. */}
          {!isNew && lead?.profile && hasQualificationProfile(lead.profile) && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">
                {t("crm.leadDetail.tripProfile")}
              </h2>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {lead.profile.tripType && (
                  <span className="rounded-full bg-purple-100 px-2 py-0.5 font-medium text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                    {PROFILE_LABELS.tripType[lead.profile.tripType] ?? lead.profile.tripType}
                  </span>
                )}
                {lead.profile.flightClass && (
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                    {PROFILE_LABELS.flightClass[lead.profile.flightClass] ??
                      lead.profile.flightClass}
                  </span>
                )}
                {lead.profile.budget && (
                  <span className="rounded-full bg-green-100 px-2 py-0.5 font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
                    {lead.profile.budget}
                  </span>
                )}
                {lead.profile.dateFlexibility && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {flexibilityLabel(lead.profile.dateFlexibility)}
                  </span>
                )}
                {lead.profile.travelExperience && (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                    {PROFILE_LABELS.travelExperience[lead.profile.travelExperience] ??
                      lead.profile.travelExperience}
                  </span>
                )}
                {Array.isArray(lead.profile.childAges) && lead.profile.childAges.length > 0 && (
                  <span className="rounded-full bg-pink-100 px-2 py-0.5 font-medium text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                    {t("crm.leadDetail.childrenAges", { ages: lead.profile.childAges.join(", ") })}
                  </span>
                )}
              </div>
              {lead.profile.routing && (
                <p className="mt-2 text-xs text-gray-400">
                  {lead.profile.routing.by === "garant"
                    ? t("crm.leadDetail.autoRoutedGarant")
                    : t("crm.leadDetail.autoRoutedRoundRobin")}
                </p>
              )}
            </div>
          )}

          {!isNew && lead && (
            <div className="card">
              <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">
                {t("crm.leadDetail.attributionSection")}
              </h2>
              <dl className="space-y-1 text-sm">
                <Row
                  label={t("crm.leadDetail.source")}
                  value={SOURCE_LABELS[lead.source] ?? lead.source}
                />
                <Row
                  label={t("crm.leadDetail.campaign")}
                  value={lead.utmCampaign || lead.firstTouchCampaign}
                />
                <Row label={t("crm.leadDetail.medium")} value={lead.utmMedium} />
                <Row label={t("crm.leadDetail.clickId")} value={lead.clickId} />
              </dl>
            </div>
          )}

          {!isNew && lead?.aiDraft?.draft && (
            <div className="card border-primary-200 dark:border-primary-900/40 border">
              <div className="mb-2 flex items-center gap-2">
                <Sparkles className="text-primary-600 dark:text-primary-400 h-5 w-5" />
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {t("crm.leadDetail.aiSuggestedReply")}
                </h2>
              </div>
              <p className="whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-sm text-gray-700 dark:bg-gray-800/50 dark:text-gray-300">
                {lead.aiDraft.draft}
              </p>
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => setPrefillReply(lead.aiDraft?.draft)}
                  disabled={!lead.contactEmail}
                  className="btn-primary btn-sm disabled:opacity-50"
                  title={!lead.contactEmail ? t("crm.leadDetail.noContactEmail") : undefined}
                >
                  <Sparkles className="h-4 w-4" /> {t("crm.leadDetail.useAsReply")}
                </button>
              </div>
            </div>
          )}

          {!isNew && lead && (
            <RecentCallsPanel
              communications={lead.communications ?? []}
              invalidateKeys={[["lead", lead.id]]}
            />
          )}

          {!isNew && lead && (
            <div className="card">
              <CommunicationTimeline
                leadId={lead.id}
                customerId={lead.customerId ?? ""}
                communications={lead.communications ?? []}
                contactEmail={lead.contactEmail ?? ""}
                prefillReply={prefillReply}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
  full,
}: {
  label: string;
  children: React.ReactNode;
  full?: boolean;
}) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="mb-1 block text-sm font-medium text-gray-600 dark:text-gray-400">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Does the profile hold anything the qualification-chips card renders? */
function hasQualificationProfile(profile: NonNullable<LeadWithRelations["profile"]>): boolean {
  return Boolean(
    profile.tripType ||
    profile.flightClass ||
    profile.budget ||
    profile.dateFlexibility ||
    profile.travelExperience ||
    profile.childAges?.length ||
    profile.routing,
  );
}

/** "Deluxe (+3 890 Kč)" / "Standard (v ceně)" — surcharges are flat amounts for the whole stay.
 *  The currency is the LEAD's (same source as the estimated value), never hardcoded. */
function withSurcharge(
  name: string,
  surcharge: number,
  includedLabel: string,
  currency: string,
): string {
  return surcharge > 0
    ? `${name} (+${formatPrice(surcharge, currency, uiLocale())})`
    : `${name} (${includedLabel})`;
}

/**
 * Období „od – do“ z ISO dat (termínové okno poptávky na míru i „Jiný termín“). Se samotným
 * začátkem („od 10. 4.") i samotným koncem („do 25. 4.") musí věta dávat smysl — zákazník
 * nemusel vyplnit obojí; bez obou `null`.
 */
function formatPeriod(fromIso?: string | null, toIso?: string | null): string | null {
  const from = fromIso ? formatDate(fromIso) : null;
  const to = toIso ? formatDate(toIso) : null;
  if (from && to) return `${from} – ${to}`;
  if (from) return i18n.t("crm.leadDetail.custom.windowFrom", { date: from });
  if (to) return i18n.t("crm.leadDetail.custom.windowTo", { date: to });
  return null;
}

/** Popisek flexibility termínu; neznámou hodnotu ukáže syrovou. */
function flexibilityLabel(value: string): string {
  return PROFILE_LABELS.dateFlexibility[value] ?? value;
}

/**
 * Termín z poptávky (F1.6): vypsaný odjezd je fakt v DB (`tripDate`, join), „Jiný termín“ je
 * snímek z `profile`. Kdyby platilo obojí, vyhrává odjezd. Starší poptávka bez obojího kartu
 * nemá, její datum nese pole „Požadovaný termín“ v sekci Zájem.
 */
function LeadDateCard({ lead }: { lead: LeadWithRelations }) {
  const { t } = useTranslation();
  const title = (
    <h2 className="mb-2 font-semibold text-gray-900 dark:text-white">
      {t("crm.leadDetail.departure.title")}
    </h2>
  );

  if (lead.tripDate) {
    const date = lead.tripDate;
    return (
      <div className="card">
        {title}
        <dl className="space-y-1 text-sm">
          <Row
            label={t("crm.leadDetail.departure.departure")}
            value={`${formatDate(date.startDate)} – ${formatDate(date.endDate)}`}
          />
          <Row
            label={t("crm.leadDetail.departure.lifecycle")}
            value={
              <span className="flex items-center gap-1">
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-medium ${TRIP_DATE_LIFECYCLE_BADGES[date.lifecycle]}`}
                >
                  {enumLabel(TRIP_DATE_LIFECYCLE_LABELS, date.lifecycle)}
                </span>
                {date.isPrivate && <PrivateDateBadge />}
              </span>
            }
          />
        </dl>
        <Link
          to={`/departures/${date.id}`}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 mt-2 inline-block text-sm"
        >
          {t("trips.dates.openVelin")}
        </Link>
      </div>
    );
  }

  if (lead.profile?.dateChoice !== "other") return null;
  return (
    <div className="card">
      {title}
      <span className="badge badge-warning mb-2">{t("crm.leadDetail.departure.notListed")}</span>
      <dl className="space-y-1 text-sm">
        <Row
          label={t("crm.leadDetail.departure.period")}
          value={formatPeriod(lead.requestedDate, lead.profile.requestedDateTo)}
        />
        <Row
          label={t("crm.leadDetail.departure.flexibility")}
          value={
            lead.profile.dateFlexibility ? flexibilityLabel(lead.profile.dateFlexibility) : null
          }
        />
      </dl>
    </div>
  );
}

function Row({ label, value }: { label: string; value?: ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-gray-500">{label}</dt>
      <dd className="font-medium text-gray-900 dark:text-white">{value || "—"}</dd>
    </div>
  );
}
